import type { MemberCarePlan, MemberCaseNotesListItem } from '@troyai/case-notes/data-access';
import type {
  CarePlanSuggestion,
  HraSummaryResponse,
  MemberHra,
  RecommendationReview,
} from '@troyai/hra/data-access';
import type { Patient } from '@troyai/patients/data-access';
import type {
  CareSignalView,
  HealthStoryCategory,
  HealthStoryEntry,
  HealthSummary,
  TimelineViewResponse,
} from '@troyai/portal/health-timeline/data-access';
import type {
  Episode,
  EpisodeEvent,
  RecommendationLifecycleEvent,
} from '@troyai/portal/intervention-episodes/data-access';
import {
  DemoPerspective,
  demoMemberId,
  demoPerspective,
  demoPersona,
  isPharmacyDemo,
  storyDay,
} from '@troyai/portal/investor-demo/core';

import { seedEpisodes } from './fixtures/episodes.fixture';
import { seedHra } from './fixtures/hra.fixture';
import { FIRST_NEW_ID } from './fixtures/ids';
import { seedHealthSummary, seedPatient } from './fixtures/patient.fixture';
import { seedPharmacyStory } from './fixtures/pharmacy/pharmacy-story';
import { seedCareSignals, seedStoryEntries } from './fixtures/timeline.fixture';

/** localStorage key of the serialised store. */
export const DEMO_DATA_KEY = 'troy-investor-demo-data';

/** A fixture item that stays hidden while the demo clock (`demoToday()`) is before `visibleFrom`. */
export type Visible<T> = T & { visibleFrom?: string }; // yyyy-MM-dd

/** An episode whose activity entries can be held back until the "+3 days" jump. */
export type DemoEpisode = Omit<Episode, 'events' | 'recommendation_lifecycle'> & {
  events: Visible<EpisodeEvent>[];
  recommendation_lifecycle: Visible<RecommendationLifecycleEvent>[];
};

/**
 * Everything the fake backend serves, as one serialisable object. Data only: the jumped clock
 * lives in the demo settings of `@troyai/portal/investor-demo/core`.
 */
export interface DemoData {
  /** The shape of the saved state; a store of another version is not read. */
  version: 5;
  /** The story that was seeded; a store of the other perspective is not read. */
  perspective: DemoPerspective;
  /** The persona's email at seed time ("you"); a store of another persona is not read. */
  personaEmail: string;
  /** The story day (`yyyy-MM-dd`) the dates were written for; a store of another day is not read. */
  storyDay: string;
  /** The member the story was seeded for; a store of another member is not read. */
  memberId: number;
  patient: Patient;
  healthSummary: HealthSummary;
  /** Newest first. The category may be `interaction` (Calls & notes). */
  storyEntries: Visible<HealthStoryEntry>[];
  careSignals: Record<CareSignalView, TimelineViewResponse>;
  episodes: DemoEpisode[];
  assessments: MemberHra[];
  recommendationReview: RecommendationReview;
  hraSummary: HraSummaryResponse;
  suggestions: CarePlanSuggestion[];
  carePlans: MemberCarePlan[];
  caseNotes: MemberCaseNotesListItem[];
  /** The category of the last clinical-evidence list read; `undefined` before the first read. */
  lastEvidenceCategory: HealthStoryCategory | null | undefined;
  /** The next id for an observation or event the demo creates; starts at `FIRST_NEW_ID`. */
  nextId: number;
}

/**
 * A fresh copy of the starting story, on the real calendar (the story day is the day the page
 * loaded): nothing activated, nothing decided.
 */
export function seedDemoData(): DemoData {
  const story = isPharmacyDemo()
    ? seedPharmacyStory()
    : {
        healthSummary: seedHealthSummary(),
        storyEntries: seedStoryEntries(),
        careSignals: seedCareSignals(),
        episodes: seedEpisodes(),
      };
  return {
    version: 5,
    perspective: demoPerspective(),
    personaEmail: demoPersona().email,
    storyDay: storyDay(),
    memberId: demoMemberId(),
    patient: seedPatient(),
    ...story,
    ...seedHra(),
    carePlans: [],
    caseNotes: [],
    lastEvidenceCategory: undefined,
    nextId: FIRST_NEW_ID,
  };
}
